import { useMsal } from '@azure/msal-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { authFetch, useToken } from '../auth/apiClient';

export interface UserRoleRef {
  id: number;
  name: string;
  is_admin: boolean;
}

export interface AdminUserRow {
  id: number;
  upn: string;
  display_name: string | null;
  is_admin: boolean;
  is_bootstrap_admin: boolean;
  roles: UserRoleRef[];
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface Role {
  id: number;
  name: string;
  permissions: string[];
  allowed_producers: string[];
}

export interface RoleInput {
  name: string;
  permissions: string[];
  allowed_producers: string[];
}

export interface PermissionInfo {
  key: string;
  label: string;
  group: string;
}

export interface ReportInfo {
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

export interface AdminUserUpdate {
  role_ids?: number[];
  is_active?: boolean;
}

export function useReportsCatalog(): UseQueryResult<ReportInfo[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['reports-catalog'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<ReportInfo[]>(await getToken(), '/api/reports/catalog'),
  });
}

export function usePermissionsCatalog(): UseQueryResult<PermissionInfo[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-permissions'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<PermissionInfo[]>(await getToken(), '/api/admin/permissions'),
  });
}

export function useProducersCatalog(): UseQueryResult<string[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-producers'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<string[]>(await getToken(), '/api/admin/producers'),
  });
}

export function useAdminUsers(): UseQueryResult<AdminUserRow[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-users'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<AdminUserRow[]>(await getToken(), '/api/admin/users'),
  });
}

export function useUpdateUser(): UseMutationResult<
  AdminUserRow,
  Error,
  { id: number; payload: AdminUserUpdate }
> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }) =>
      authFetch<AdminUserRow>(await getToken(), `/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRoles(): UseQueryResult<Role[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-roles'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<Role[]>(await getToken(), '/api/admin/roles'),
  });
}

export function useCreateRole(): UseMutationResult<Role, Error, RoleInput> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) =>
      authFetch<Role>(await getToken(), '/api/admin/roles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
  });
}

export function useUpdateRole(): UseMutationResult<
  Role,
  Error,
  { id: number; body: Partial<RoleInput> }
> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, body }) =>
      authFetch<Role>(await getToken(), `/api/admin/roles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useDeleteRole(): UseMutationResult<unknown, Error, number> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) =>
      authFetch<unknown>(await getToken(), `/api/admin/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export type DataSourceStatus = 'empty' | 'ok' | 'error';

export interface DataSourceRow {
  code: string;
  name: string;
  description: string | null;
  last_refreshed_at: string | null;
  next_run_at: string | null;
  status: DataSourceStatus;
  error: string | null;
  row_count: number | null;
}

export function useDataSources(enabled = true): UseQueryResult<DataSourceRow[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-data-sources'],
    enabled: enabled && accounts.length > 0,
    queryFn: async () =>
      authFetch<DataSourceRow[]>(await getToken(), '/api/admin/data-sources'),
    refetchInterval: 15_000,
  });
}

export function useRefreshAllDataSources(): UseMutationResult<
  { scheduled: string[] },
  Error,
  void
> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      authFetch<{ scheduled: string[] }>(
        await getToken(),
        '/api/admin/data-sources/refresh-all',
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-data-sources'] });
    },
  });
}

export function useRefreshDataSource(): UseMutationResult<unknown, Error, string> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code) =>
      authFetch<unknown>(await getToken(), `/api/admin/data-sources/${code}/refresh`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-data-sources'] });
    },
  });
}

export interface LastUnitAlertResult {
  day: string;
  count: number;
  posted: boolean;
}

export function useTestLastUnitAlert(): UseMutationResult<LastUnitAlertResult, Error, void> {
  const getToken = useToken();

  return useMutation({
    mutationFn: async () =>
      authFetch<LastUnitAlertResult>(await getToken(), '/api/admin/alerts/last-unit/test', {
        method: 'POST',
      }),
  });
}

export interface R1ReportSettings {
  external_doc_symbols: string[];
  updated_at: string | null;
  updated_by: string | null;
}

export function useR1Settings(): UseQueryResult<R1ReportSettings> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-r1-settings'],
    enabled: accounts.length > 0,
    queryFn: async () =>
      authFetch<R1ReportSettings>(await getToken(), '/api/admin/data-sources/r1/settings'),
  });
}

export function useSaveR1Settings(): UseMutationResult<R1ReportSettings, Error, string[]> {
  const getToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (symbols) =>
      authFetch<R1ReportSettings>(await getToken(), '/api/admin/data-sources/r1/settings', {
        method: 'PUT',
        body: JSON.stringify({ external_doc_symbols: symbols }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-r1-settings'] });
    },
  });
}

// --- Audit log (FR-AUDIT) ---

export interface AuditEntry {
  id: number;
  ts: string;
  actor_upn: string | null;
  event_type: string;
  object_type: string | null;
  object_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  source: string;
  ip: string | null;
  detail: string | null;
}

export interface AuditPage {
  rows: AuditEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditQuery {
  actor: string;
  eventType: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  pageSize: number;
}

function auditParams(q: AuditQuery): URLSearchParams {
  const qs = new URLSearchParams();
  if (q.actor) qs.set('actor', q.actor);
  if (q.eventType) qs.set('event_type', q.eventType);
  if (q.search) qs.set('search', q.search);
  if (q.dateFrom) qs.set('date_from', q.dateFrom);
  if (q.dateTo) qs.set('date_to', q.dateTo);
  return qs;
}

export function useAuditLog(q: AuditQuery): UseQueryResult<AuditPage> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-audit', q],
    enabled: accounts.length > 0,
    queryFn: async () => {
      const qs = auditParams(q);
      qs.set('page', String(q.page));
      qs.set('page_size', String(q.pageSize));
      return authFetch<AuditPage>(await getToken(), `/api/admin/audit?${qs.toString()}`);
    },
    placeholderData: (prev) => prev,
  });
}

export function useAuditEventTypes(): UseQueryResult<string[]> {
  const getToken = useToken();
  const { accounts } = useMsal();

  return useQuery({
    queryKey: ['admin-audit-event-types'],
    enabled: accounts.length > 0,
    queryFn: async () => authFetch<string[]>(await getToken(), '/api/admin/audit/event-types'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useExportAudit(): (q: AuditQuery) => Promise<void> {
  const getToken = useToken();
  return async (q) => {
    const qs = auditParams(q);
    const response = await fetch(`/api/admin/audit/export?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${await getToken()}` },
    });
    if (!response.ok) {
      throw new Error(`GET /api/admin/audit/export → ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-log.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
}

